// src/pages/Account/AccountProfilePage.jsx
import { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, CircularProgress, Divider, Chip, alpha } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../axiosInstance';
import { toast } from 'react-toastify';

export default function AccountProfilePage() {
  const { user, refreshAuthData } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    });
    setLoading(false);
  }, [user]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onSave = async () => {
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.info('Nombre y correo son requeridos');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.patch('/user/', {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email, // si tu backend valida email único, cuidarlo
      });
      toast.success('Perfil actualizado');
      await refreshAuthData?.();
    } catch (e) {
      const data = e?.response?.data || {};
      const errMsg = data?.email?.[0] || data?.detail || data?.error || 'No se pudo actualizar el perfil';
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>Mi Cuenta</Typography>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: '16px',
          borderColor: (t) => alpha(t.palette.divider, 0.8),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
          <Chip label={user?.rol || 'Usuario'} color="primary" variant="outlined" />
          {user?.require_password_change && (
            <Chip label="Debe cambiar contraseña" color="warning" variant="filled" />
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Nombres"
            name="first_name"
            value={form.first_name}
            onChange={onChange}
            size="small"
            fullWidth
          />
          <TextField
            label="Apellidos"
            name="last_name"
            value={form.last_name}
            onChange={onChange}
            size="small"
            fullWidth
          />
          <TextField
            label="Correo"
            name="email"
            value={form.email}
            onChange={onChange}
            size="small"
            fullWidth
          />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setForm({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
              });
            }}
          >
            Deshacer cambios
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            startIcon={saving ? <CircularProgress size={16} /> : null}
            disabled={saving}
          >
            Guardar
          </Button>
          <Button
            variant="text"
            color="secondary"
            href="/account/password"
          >
            Cambiar contraseña
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
